import gsap, { Flip } from "@lib/gsap";
import {
  isHome,
  isCatalogue,
  isProduct,
  isStudio,
  isLookbook,
  ignoreElementsFromSelector,
} from "@/utils/routes";

export class Nav {
  constructor() {
    this.nav = document.querySelector("[data-module='nav']");
    this.desktopNavMain = this.nav?.querySelector("[data-nav-desktop-main]");
    this.desktopNavMainItems = this.nav?.querySelectorAll(
      "[data-nav-desktop-main] [data-nav-item]"
    );
    this.desktopNavMainGroupItems = this.nav?.querySelectorAll(
      "[data-nav-desktop-main-group] [data-nav-item]"
    );

    this.desktopNavOthers = this.nav?.querySelector(
      "[data-nav-desktop-others]"
    );
    this.desktopNavOthersItems = this.nav?.querySelectorAll(
      "[data-nav-desktop-others] [data-nav-item]"
    );

    this.navNumEl = this.nav?.querySelector("[data-nav-num]");

    this.navNumSuffixes = this.nav?.querySelectorAll("[data-nav-num] .suffix");

    this.menu = this.nav?.querySelector("[data-menu]");
    this.menuClose = this.nav?.querySelector("[data-menu-close]");
    this.menuOpen = this.nav?.querySelector("[data-menu-open]");

    this.navClip = this.nav?.querySelector(".m-nav-inner-clip");
  }

  addEventListeners() {
    this.menuOpen.addEventListener("click", () => {
      this.openMenu();
    });

    this.menuClose.addEventListener("click", () => {
      this.closeMenu();
    });
  }

  openMenu() {
    this.menu.classList.remove("hidden");
    const menuItems = this.menu.querySelectorAll("[data-nav-item]");

    gsap.to(this.menu, {
      opacity: 1,
      duration: 0.2,
    });

    gsap.from(menuItems, {
      y: 10,
      opacity: 0,
      ease: "smooth",
      stagger: 0.1,
      clearProps: "all",
    });
  }

  closeMenu() {
    gsap.to(this.menu, {
      opacity: 0,
      duration: 0.1,

      onComplete: () => {
        this.menu.classList.add("hidden");
      },
    });
  }

  setItemsVisibility(items, hide) {
    items.forEach((item) => {
      if (hide) {
        item.classList.add("hidden");
      } else {
        item.classList.remove("hidden");
      }
    });
  }

  showNavNumEl() {
    const currentUrl = window.location.pathname;
    const mainNavIndex = [...this.desktopNavMainGroupItems].findIndex(
      (item) => item.getAttribute("href") === currentUrl
    );
    const currentSuffix = this.navNumSuffixes[mainNavIndex];

    const otherSuffixes = [...this.navNumSuffixes].filter(
      (suffix) => suffix !== currentSuffix
    );
    gsap.set(otherSuffixes, {
      opacity: 0,
    });

    if (currentSuffix) {
      gsap.set(currentSuffix, {
        opacity: 1,
      });
    }

    this.showNavNumElAnimation = gsap.to(this.navNumEl, {
      opacity: 1,
    });
  }

  hideNavNumEl() {
    this.hideNavNumElAnimation = gsap.to(this.navNumEl, {
      opacity: 0,
      duration: 0.4,
    });
  }

  transitionSuffixes(previousUrl) {
    const currentUrl = window.location.pathname;
    const mainNavIndex = [...this.desktopNavMainGroupItems].findIndex(
      (item) => item.getAttribute("href") === currentUrl
    );
    const currentSuffix = this.navNumSuffixes[mainNavIndex];

    const previousMainNavIndex = [...this.desktopNavMainGroupItems].findIndex(
      (item) => item.getAttribute("href") === previousUrl
    );
    const previousSuffix = this.navNumSuffixes[previousMainNavIndex];

    if (previousSuffix) {
      this.transitionSuffixesPreviousAnimation = gsap.to(previousSuffix, {
        opacity: 0,
        yPercent: 10,
        duration: 0.2,
        ease: "smooth",
        clearProps: "transform",
      });
    }

    if (currentSuffix) {
      this.transitionSuffixesNextAnimation = gsap.fromTo(
        currentSuffix,
        {
          opacity: 0,
          yPercent: -10,
        },
        {
          opacity: 1,
          ease: "smooth",
          yPercent: 0,
        }
      );
    }
  }

  checkGridIntersection() {
    const gridParent = document.querySelector("[data-module='product-grids']");

    if (!gridParent) {
      return;
    }

    // check if grid parent is intersecting with the viewport using the intersection observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          this.transitionMenuOpenButton();
        }
      });

      observer.disconnect();
    });
    observer.observe(gridParent);
  }

  transitionMenuOpenButton() {
    gsap.set(this.menuOpen, {
      top: 14,
      opacity: 1,
    });

    gsap.from(this.menuOpen, {
      y: 10,
      opacity: 0,
      ease: "smooth",
      onComplete: () => {
        gsap.set(this.menuOpen, {
          clearProps: "top",
          y: 14,
        });
      },
    });
  }

  moveMenuOpenButtonDown() {
    this.navClip.classList.add("hidden");
    gsap.to(this.menuOpen, {
      y: 14,
      ease: "smooth",
    });
  }

  moveMenuOpenButtonUp() {
    this.navClip.classList.remove("hidden");
    gsap.to(this.menuOpen, {
      y: 0,
      ease: "smooth",
    });
  }

  init() {
    this.setActiveNavItem();
    this.setpageUrl(window.location.pathname);
    this.addEventListeners();
    // this.navClip.classList.add("hidden");

    if (isHome() || isLookbook()) {
      console.log("home or lookbook");
      this.setItemsVisibility(this.desktopNavMainItems, false);
      this.setItemsVisibility(this.desktopNavOthersItems, true);

      gsap.set(this.menuOpen, {
        opacity: 0,
      });

      this.checkGridIntersection();

      gsap.from(this.desktopNavMainItems, {
        y: 10,
        opacity: 0,
        stagger: 0.1,
        clearProps: "all",
        ease: "smooth",
      });

      gsap.set(this.nav, {
        visibility: "visible",
      });

      return;
    }

    this.transitionMenuOpenButton();

    if (isCatalogue()) {
      this.showNavNumEl();
    }

    if (isCatalogue() || isStudio()) {
      const ignoredElements = ignoreElementsFromSelector(
        Array.from(this.desktopNavMainItems),
        Array.from(this.desktopNavMainGroupItems)
      );
      this.setItemsVisibility(this.desktopNavOthersItems, isStudio());
      this.setItemsVisibility(ignoredElements, false);
      this.setItemsVisibility(this.desktopNavMainGroupItems, true);

      gsap.set(ignoredElements, {
        top: (index) => {
          if (index === 0) {
            return 52;
          }

          return -52;
        },
      });

      // gsap.set([lastIgnoredElement, ignoredElementBeforeLast], {
      //   top: -52,
      // });

      gsap.from(ignoredElements, {
        y: 10,
        opacity: 0,
        stagger: 0.1,
        ease: "smooth",
        onComplete: () => {
          gsap.set(ignoredElements, {
            clearProps: "top",
            y: (index) => {
              if (index === 0) {
                return 52;
              }
              return -52;
            },
          });
        },
      });

      gsap.from(this.desktopNavOthersItems, {
        y: 10,
        opacity: 0,
        stagger: 0.1,
        clearProps: "all",
        ease: "smooth",
      });

      gsap.set(this.nav, {
        visibility: "visible",
      });
    }

    if (isProduct()) {
      this.setItemsVisibility(this.desktopNavMainItems, true);
      this.setItemsVisibility(this.desktopNavOthersItems, true);

      gsap.set(this.nav, {
        visibility: "visible",
      });
    }
  }

  setActiveNavItem() {
    const pathname = window.location.pathname;

    const activeNavItem = document.querySelectorAll(
      `[data-nav-item][href="${pathname}"]`
    );

    const allNavItems = document.querySelectorAll("[data-nav-item]");
    allNavItems.forEach((item) => {
      item.classList.remove("active");
      item.classList.remove("transparent");
    });

    if (activeNavItem) {
      activeNavItem.forEach((item) => {
        item.classList.add("active");
      });
    }

    if (isCatalogue()) {
      const activeNavItem = document.querySelectorAll(
        `[data-nav-item][href="/catalogue"]`
      );

      activeNavItem.forEach((item) => {
        item.classList.add("active");
      });
    }
  }

  setpageUrl(url) {
    document
      .querySelector("[data-taxi-view]")
      .setAttribute("data-taxi-url", url);
  }

  collapseMainNav() {
    const ignoredElements = ignoreElementsFromSelector(
      Array.from(this.desktopNavMainItems),
      Array.from(this.desktopNavMainGroupItems)
    );

    this.collapseMainNavAnimation = gsap.to(ignoredElements, {
      y: (index) => {
        if (index === 0) {
          return 52;
        }

        return -52;
      },
      ease: "smooth",
    });
  }

  expandMainNav() {
    const ignoredElements = ignoreElementsFromSelector(
      Array.from(this.desktopNavMainItems),
      Array.from(this.desktopNavMainGroupItems)
    );

    this.expandMainNavAnimation = gsap.to(ignoredElements, {
      y: 0,
      ease: "smooth",
    });
  }

  fadeIgnoredElementsIn() {
    const ignoredElements = ignoreElementsFromSelector(
      Array.from(this.desktopNavMainItems),
      Array.from(this.desktopNavMainGroupItems)
    );

    ignoredElements.forEach((item) => {
      item.classList.remove("hidden");
    });

    gsap.set(ignoredElements, {
      y: (index) => {
        if (index === 0) {
          return 52;
        }

        return -52;
      },
    });

    this.fadeIgnoredElementsInAnimation = gsap.to(ignoredElements, {
      opacity: 1,
      ease: "smooth",
      stagger: 0.1,
    });
  }

  flipMainToOtherNav(reverse = false) {
    const currentUrl = window.location.pathname;
    const mainNavIndex = [...this.desktopNavMainGroupItems].findIndex(
      (item) => item.getAttribute("href") === currentUrl
    );
    const mainNavItem = [...this.desktopNavMainGroupItems][mainNavIndex];

    // remove item from array and add it to the beginning
    const newMainNavItems = [...this.desktopNavMainGroupItems].filter(
      (item) => item !== mainNavItem
    );
    newMainNavItems.unshift(mainNavItem);

    this.desktopNavOthersItems.forEach((item, index) => {
      const mainNavItem = [...this.desktopNavMainGroupItems][index];

      if (mainNavItem) {
        mainNavItem.dataset.flipId = index;
        item.dataset.flipId = index;

        if (reverse) {
          const state = Flip.getState([item, mainNavItem]);

          mainNavItem.classList.remove("hidden");
          item.classList.add("hidden");

          gsap.set(mainNavItem, {
            opacity: 1,
          });

          gsap.set(mainNavItem.querySelector("[data-nav-item-hide]"), {
            opacity: 0,
          });

          Flip.from(state, {
            ease: "smooth",
            delay: index * 0.1,
            onComplete: () => {
              gsap.to(mainNavItem.querySelector("[data-nav-item-hide]"), {
                opacity: 1,
                duration: 0.5,
              });
            },
          });

          return;
        }

        const state = Flip.getState([mainNavItem, item]);

        const mainNavIndex = newMainNavItems.indexOf(mainNavItem);

        mainNavItem.classList.add("hidden");
        item.classList.remove("hidden");

        gsap.set(item, {
          opacity: 1,
        });

        gsap.set(item.querySelector("[data-nav-item-hide]"), {
          opacity: 0,
        });

        Flip.from(state, {
          // duration: 0.8,
          ease: "smooth",
          delay: mainNavIndex * 0.1,
          // flipSort: false,
          onComplete: () => {
            gsap.to(item.querySelector("[data-nav-item-hide]"), {
              opacity: 1,
              duration: 0.5,
            });
          },
        });
      }
    });
  }

  fadeOtherNavsIn() {
    this.desktopNavOthersItems.forEach((item) => {
      item.classList.remove("hidden");
    });

    this.fadeOthersNavsInAnimation = gsap.fromTo(
      this.desktopNavOthersItems,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        ease: "smooth",
        stagger: 0.1,
      }
    );
  }

  fadeOthersNavsOut() {
    this.fadeOthersNavsOutAnimation = gsap.to(this.desktopNavOthersItems, {
      opacity: 0,
      ease: "smooth",
      stagger: 0.1,
      onComplete: () => {
        this.desktopNavOthersItems.forEach((item) => {
          item.classList.add("hidden");
        });
      },
    });
  }

  fadeMainNavsIn() {
    this.desktopNavMainItems.forEach((item) => {
      item.classList.remove("hidden");
    });

    this.fadeMainNavsInAnimation = gsap.fromTo(
      this.desktopNavMainItems,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        ease: "smooth",
        stagger: 0.1,
      }
    );
  }

  fadeMainNavsOut() {
    this.fadeMainNavsOutAnimation = gsap.to(this.desktopNavMainItems, {
      opacity: 0,
      ease: "smooth",
      stagger: 0.1,
      onComplete: () => {
        this.desktopNavMainItems.forEach((item) => {
          item.classList.add("hidden");
        });
      },
    });
  }

  fadeNavGroupIn() {
    this.desktopNavMainGroupItems.forEach((item) => {
      item.classList.remove("hidden");
    });

    this.fadeNavGroupInAnimation = gsap.fromTo(
      this.desktopNavMainGroupItems,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        x: -10,
        ease: "smooth",
        stagger: 0.1,
        onComplete: () => {
          this.desktopNavMainGroupItems.forEach((item) => {
            item.classList.remove("hidden");
          });
        },
      }
    );
  }

  fadeNavGroupOut() {
    this.fadeNavGroupOutAnimation = gsap.to(this.desktopNavMainGroupItems, {
      opacity: 0,
      x: 10,
      duration: 0.4,
      ease: "smooth",
      stagger: 0.1,
      onComplete: () => {
        this.desktopNavMainGroupItems.forEach((item) => {
          item.classList.add("hidden");
        });
      },
    });
  }

  handleCatalogueRouteChange(previousUrl) {
    this.showNavNumEl();
    // coming from home to catalogue
    if (isHome(previousUrl) || isLookbook(previousUrl)) {
      this.flipMainToOtherNav();
      setTimeout(() => {
        this.collapseMainNav();
      }, 300);

      return;
    }

    if (isStudio(previousUrl)) {
      this.fadeOtherNavsIn();

      return;
    }

    if (isCatalogue(previousUrl)) {
      this.transitionSuffixes(previousUrl);
      return;
    }

    // if (isProduct(previousUrl)) {
    this.fadeOtherNavsIn();
    this.fadeIgnoredElementsIn();

    // this.fadeMainNavsIn();
    // }
  }

  handleStudioRouteChange(previousUrl) {
    if (isHome(previousUrl) || isLookbook(previousUrl)) {
      this.fadeNavGroupOut();
      setTimeout(() => {
        this.collapseMainNav();
      }, 300);
      return;
    }

    if (isCatalogue(previousUrl)) {
      this.fadeOthersNavsOut();
      return;
    }
  }

  handleHomeRouteChange(previousUrl) {
    if (isCatalogue(previousUrl)) {
      this.expandMainNav();
      this.flipMainToOtherNav(true);
      return;
    }

    if (isStudio(previousUrl)) {
      this.expandMainNav();
      this.fadeNavGroupIn();
      return;
    }

    this.expandMainNav();
    this.fadeMainNavsIn();
  }

  handleProductRouteChange() {
    this.fadeOthersNavsOut();
    this.fadeMainNavsOut();
  }

  killAnimations() {
    this.collapseMainNavAnimation?.kill();
    this.expandMainNavAnimation?.kill();
    this.fadeOthersNavsInAnimation?.kill();
    this.fadeOthersNavsOutAnimation?.kill();
    this.fadeMainNavsInAnimation?.kill();
    this.fadeMainNavsOutAnimation?.kill();
    this.fadeNavGroupInAnimation?.kill();
    this.fadeNavGroupOutAnimation?.kill();
    this.fadeIgnoredElementsInAnimation?.kill();
    this.showNavNumElAnimation?.kill();
    this.hideNavNumElAnimation?.kill();
    this.transitionSuffixesPreviousAnimation?.kill();
    this.transitionSuffixesNextAnimation?.kill();
  }

  handleRouteChange(previousUrl) {
    this.killAnimations();
    this.moveMenuOpenButtonDown();

    if (isHome() || isLookbook()) {
      this.hideNavNumEl();
      this.handleHomeRouteChange(previousUrl);
    }

    if (isCatalogue()) {
      this.handleCatalogueRouteChange(previousUrl);
    }

    if (isStudio()) {
      this.hideNavNumEl();
      this.handleStudioRouteChange(previousUrl);
    }

    if (isProduct()) {
      this.hideNavNumEl();
      this.handleProductRouteChange();
    }
  }
}
